/**
 * @file Controller for a 2D sprite
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2309.17.001
 */

/** @module sprite */

import { Game } from "./Game.js";
import { Box } from "./Box.js";

/** 
 * A representation of a 2D sprite. 
 * @class
 * @augments module:box~Box
 */
class Sprite extends Box {
	
	/**
	 * Rows of a character spritesheet.
	 * @typedef {Object} module:sprite~Sprite.Facing
	 * @property {Number} DOWN 0
	 * @property {Number} LEFT 1
	 * @property {Number} RIGHT 2
	 * @property {Number} UP 3
	 * @property {Number} FRONT 4
	 * @property {Number} LEFT_PROFILE 5
	 * @property {Number} RIGHT_PROFILE 6
	 * @property {Number} BACK 7
	 **/

	/**
	 * Rows of a character spritesheet.
	 * @memberof module:sprite~Sprite
	 * @static
	 * @readonly
	 * @enum {module:sprite~Sprite.Facing}
	 */
	static Facing = Object.freeze( Object.create( null, {
		// Moving
		"DOWN":  { value: 0 },
		"LEFT":  { value: 1 },
		"RIGHT": { value: 2 },
		"UP":    { value: 3 },
		// Resting
		"FRONT":         { value: 4 },
		"LEFT_PROFILE":  { value: 5 },
		"RIGHT_PROFILE": { value: 6 },
		"BACK":          { value: 7 }
	} ) );

	/**
	 * Default values for a sprite's options.
	 * @memberof module:sprite~Sprite
	 * @static
	 * @readonly
	 * @type {Object}
	 */
	static DefaultOptions = Object.freeze( {
		hitBox: null,
		animationInterval: 100,
		animationIntervalRange: null,
		animationSpeedFactor: 1,
		direction: Sprite.Facing.DOWN,
		directionChangeInterval: 3000,
		framesRowIndex: 0, // Equivalent to Sprite.Facing.DOWN
		frameNumber: 0,
		randomFrames: false,
		movementArea: null,
		cycleHue: false
	}	);

	/** 
	 * The spritesheet image.
	 * @type {Image?} 
	 */
	#image = null;
	/** 
	 * Context where the sprite is to be drawn.
	 * @type {CanvasRenderingContext2D?} 
	 */
	#context = null;
	/** 
	 * The number of frames the sprite's image provides.
	 * Determined dividing the width of the image by the width of the sprite's box.
	 * @type {Number} 
	 * @default 1
	 */
	#numberOfFrames = 1;
	/** 
	 * The row of the sprite image used for the animation loop.
	 * Rows are determined dividing the height of the image by the height of the 
	 * sprite's box.
	 * @type {Number} 
	 * @default 0
	 * @see {@link module:sprite~Sprite.DefaultOptions Sprite.DefaultOptions.framesRowIndex}
	 */
	#framesRowIndex = Sprite.DefaultOptions.framesRowIndex * 1;
	/** 
	 * The number of the frame the sprite is in. Can go up to `[numberOfFrames]{@link module:sprite~Sprite#numberOfFrames} - 1`.
	 * @type {Number} 
	 * @default 0
	 * @see {@link module:sprite~Sprite.DefaultOptions Sprite.DefaultOptions.frameNumber}
	 */
	#frameNumber = Sprite.DefaultOptions.frameNumber * 1;
	/** 
	 * Miliseconds between frames
	 * @type {Number} 
	 */
	#animationInterval = Sprite.DefaultOptions.animationInterval * 1;
	/** @type {Number[]?} */
	#animationIntervalRange = Sprite.DefaultOptions.animationIntervalRange;
	/** @type {Boolean} */
	#randomFrames = Sprite.DefaultOptions.randomFrames;
	/** @type {Number} */
	#lastTimestamp = 0;
	/** 
	 * An aggregator of the time spanned within an animation cycle.
	 * @type {Number} 
	 */
	#animationTimeSpan = 0;
	/** 
	 * A multiplier for the animation of the sprite.
	 * @type {Number} 
	 */
	#animationSpeedFactor = Sprite.DefaultOptions.animationSpeedFactor * 1;
	/** @type {Boolean} */
	#hasOrientation = false;
	/** @type {module:box~Box?} */
	#hitBox = Sprite.DefaultOptions.hitBox;
	/** @type {module:box~Box?} */
	#movementArea = Sprite.DefaultOptions.movementArea;
	/** @type {Boolean} */
	#cycleHue = !!Sprite.DefaultOptions.cycleHue;
	/** @type {Number} */
	#directionChangeInterval = Sprite.DefaultOptions.directionChangeInterval * 1;
	/** @type {Number[]} */
	#directionCounters = [ 0, 0, 0, 0 ];

	/** 
	 * Context where the sprite is to be drawn.
	 * @type {CanvasRenderingContext2D} 
	 */
	get context() { return this.#context; }
	set context( value ) {
		if ( !( value instanceof CanvasRenderingContext2D ) ) { return; }
		this.#context = value;
	}

	/** 
	 * The row of the sprite image used for the animation loop.
	 * Rows are determined dividing the height of the image by the height of the 
	 * sprite's box.
	 * @type {Number} 
	 */
	get framesRowIndex() { return this.#framesRowIndex; }
	set framesRowIndex( value ) { 
		// TODO: Check if value is valid
		this.#framesRowIndex = parseInt( value );
	}

	/** 
	 * The direction the sprite is facing. 
	 * Changing it alters [`framesRowIndex`]{@link module:sprite~Sprite#framesRowIndex} and increments a counter, tracking the number of times the sprite has faced each direction.
	 * @type {Number}  
	 */
	get direction() { return this.framesRowIndex; }
	set direction( value ) { 
		if ( value === this.#framesRowIndex ) { return; }
		this.#directionCounters[ value ] += 1;
		this.framesRowIndex = value; 
	}

	/** 
	 * The number of the frame the sprite is in, starting at zero. It cannot be
	 * more than the total number of frames, determined dividing the width of the 
	 * image by the width of the sprite's box.
	 * @type {Number}  
	 */
	get frameNumber() { return this.#frameNumber; }
	set frameNumber( value ) { 
		if (
			   ( value < 0 )
			|| ( value >= this.#numberOfFrames ) 
		) {
			return;	
		}
		this.#frameNumber = value; 
	}

	/** 
	 * A multiplier for the animation of the sprite. Cannot be less than zero.
	 * This value does _not_ affect the sprite's movement.
	 * @see {@link module:sprite~Sprite#cycleFrames}
	 * @type {Number}  
	 */
	get animationSpeedFactor() { return this.#animationSpeedFactor; }
	set animationSpeedFactor( value ) { 
		if ( isNaN( value ) ) { return; }
		if ( value < 0 ) { value = 0; }
		this.#animationSpeedFactor = parseInt( value );
	}

	/**
	 * @readonly 
	 * @type {Number} 
	 */
	get directionChangeInterval() { return this.#directionChangeInterval; }

	/**
	 * @readonly 
	 * @type {Number} 
	 */
	get leastUsedDirection() {
		return this.#directionCounters.reduce( 
			( minIndex, currentValue, currentIndex, array ) => 
				currentValue < array[ minIndex ] ? currentIndex : minIndex
			, 
			0 
		);
	}

	/**
	 * The collision area of a sprite, positioned relative to its frame. 
	 * @type {module:box~Box} 
	 */
	get hitBox() { return this.#hitBox; }
	set hitBox( value ) { 
		const box = Box.createFrom( value );
		if ( !( box instanceof Box ) ) { return; }
		this.#hitBox = box;
	}

	/** 
	 * The collision area of a sprite, in absolute coordinates.
	 * @readonly
	 * @type {module:box~Box}
	 */
	get absoluteHitBox() { 
		return ( new Box(
			this.x + this.#hitBox.x,
			this.y + this.#hitBox.y,
			this.#hitBox.width, 
			this.#hitBox.height
		) );
	}

	/** 
	 * The sprite's image.
	 * @type {Image?}
	 */
	get image() { return this.#image; }
	set image( value ) { this.#image = value; }

	/**
	 * Get the facing value representing the opposite direction to the given value
	 * @param {module:sprite~Sprite.Facing} facing 
	 * @returns {module:sprite~Sprite.Facing}
	 */
	static getOppositeDirection( facing ) {
		switch( facing ) {
			case Sprite.Facing.UP: return Sprite.Facing.DOWN; break;
			case Sprite.Facing.DOWN: return Sprite.Facing.UP; break;
			case Sprite.Facing.LEFT: return Sprite.Facing.RIGHT; break;
			case Sprite.Facing.RIGHT: return Sprite.Facing.LEFT; break;
		}
		return facing;
	}

	/**
	 * Linear intERPolation
	 * @param {Number} a 
	 * @param {Number} b 
	 * @param {Number} t 
	 * @returns {Number}
	 */
	#lerp( a, b, t ) { return ( a + t * ( b - a ) ); }

	/**
	 * Change the sprite frame based on `timestamp` and the object's 
	 * [`animationSpeedFactor`]{@link module:sprite~Sprite#animationSpeedFactor}.
	 * @param {Number} timestamp 
	 */
	cycleFrames( timestamp ) {

		// No animation
		if ( !this.#animationInterval ) { return; }

		// No frames or sped down to a halt
		if ( ( this.#numberOfFrames < 2 ) || ( this.#animationSpeedFactor <= 0 ) ) { 
			this.#frameNumber = 0;
			return; 
		}

		// How much time has passed since the last timestamp
		const timeDelta = timestamp - this.#lastTimestamp;
		// Increase the sprites time counter, considering the speed factor given
		this.#animationTimeSpan += timeDelta * this.#animationSpeedFactor;
		this.#lastTimestamp = timestamp;
		// Determine the animation interval threshold
		const animationIntervalThreshold = this.#animationIntervalRange
			? this.#lerp( ...this.#animationIntervalRange, Math.random() )
			: this.#animationInterval
		;
		// Wait for the next cycle if the elapsed time has not crossed the threshold 
		if (
			   this.#animationTimeSpan
			&& ( this.#animationTimeSpan < animationIntervalThreshold )
		) {
			return;
		}

		// Threshold crossed, reset time counter first of all
		this.#animationTimeSpan = 0;

		// Determine next frame, either random...
		if ( this.#randomFrames ) {
			this.#frameNumber = Math.round( 
				Math.random() * ( this.#numberOfFrames - 1 ) 
			);
			return;
		}
		// or the next one in the sequence
		this.#frameNumber += 1;
		if ( this.#frameNumber == this.#numberOfFrames ) { this.#frameNumber = 0; }

	}

	/**
	 * Update the sprite's position.
	 * @param   {Object} delta 
	 * @param   {Number} timestamp 
	 */
	move( delta, timestamp ) {

		if ( ( delta.x === 0 ) && ( delta.y === 0 ) ) { 
			this.#frameNumber = 0;
			return; 
		}
		
		if ( this.#hasOrientation ) {
			if ( delta.y > 0 ) { this.direction = Sprite.Facing.DOWN; }
			if ( delta.y < 0 ) { this.direction = Sprite.Facing.UP; }
			if ( delta.x < 0 ) { this.direction = Sprite.Facing.LEFT; }
			if ( delta.x > 0 ) { this.direction = Sprite.Facing.RIGHT; }
		}

		this.x += delta.x;
		this.y += delta.y;

		this.cycleFrames( timestamp );
	
		if ( 
			   ( this.#movementArea !== null ) 
			&& !this.isInside( this.#movementArea ) 
		) {
			return Game.CollisionFlag.OBSTACLE;
		}

		return Game.CollisionFlag.NONE;

	}

	/**
	 * Make the sprite face the opposite direction.
	 */
	turnAround() { 
		this.direction = Sprite.getOppositeDirection( this.direction );
	}

	/**
	 * Draw the sprite on the context.
	 * @param {Number} timestamp 
	 */
	draw( timestamp ) {
		if ( this.#cycleHue ) {
			this.#context.save();
			this.#context.filter = ""
				+ "hue-rotate(" + ( timestamp / 50 ) + "deg)"
			;
		}
		this.#context.drawImage( 
			this.#image, 
			this.#frameNumber * this.width, 
			this.#framesRowIndex * this.height,
			this.width, 
			this.height,
			this.x, 
			this.y,
			this.width, 
			this.height
		);
		if ( this.#cycleHue ) {
			this.#context.restore();
		}
	}

	/**
	 * Create a 2D sprite.
	 * Origin of coordinates is at the top left, with x increasing to the right
	 * and y increasing downwards.
	 * @param {Number} [x=0]      
	 * @param {Number} [y=0]
	 * @param {Number} [width=0]  Cannot be a negative number.
	 * @param {Number} [height=0] Cannot be a negative number.
	 * @param {Object} [options]
	 */
	constructor( x, y, width, height, image, options = {} ) {

		// Instantiate the bounding/frame box
		super( x, y, width, height );

		this.#image = image;

		if ( !options.hasOwnProperty( "hitBox" ) || !options.hitBox ) {
			options.hitBox = new Box ( 0, 0, this.width, this.height );
		}
		this.hitBox = options.hitBox;

		if ( !( this.#hitBox instanceof Box ) ) {
			console.error( "Invalid hit box" );
			return false;
		}

		let optionsDirection = options.direction;

		options = Object.assign( {}, Sprite.DefaultOptions, options );

		this.#numberOfFrames = ( this.#image.width / this.width );
		this.#numberOfFrames = Math.round( this.#numberOfFrames );

		this.#hasOrientation = this.#image.height >= 4 * this.height;

		this.#animationInterval = options.animationInterval;
		if (!this.#animationInterval) { this.#frameNumber = 0; }

		this.#framesRowIndex = options.framesRowIndex;
		if ( optionsDirection ) { 
			this.direction = Sprite.Facing[ optionsDirection ];
		}

		this.#frameNumber = options.frameNumber;

		this.#randomFrames = options.randomFrames;

		this.#animationIntervalRange = options.animationIntervalRange;

		this.#movementArea = options.hasOwnProperty( "movementArea" ) 
			? Box.createFrom( options.movementArea )
			: null
		;

		this.#directionChangeInterval = options.directionChangeInterval;

		this.#cycleHue = options.cycleHue;
		
	}

}

export { Sprite }